// src/features/clients/ClientsTab.jsx
import React, { useState, useEffect } from "react";
import { useClients } from "./hooks/useClients";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "components/ui/table";
import { Search, Users, ArrowRightLeft, Edit, Trash2 } from "lucide-react";
import ClientsMigrationTool from "./temp/ClientsMigrationTool";
import EditClientModal from "./components/EditClientModal";

function ClientsTab() {
  const { clients, isLoading, fetchClients, deleteClient, addClient } =
    useClients();
  const [searchTerm, setSearchTerm] = useState("");
  const [showMigration, setShowMigration] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.includes(searchTerm) ||
      (client.cpf && client.cpf.includes(searchTerm))
  );

  const handleUpdateClient = async (data) => {
    const success = await addClient(data);
    if (success) {
      fetchClients();
      setEditingClient(null);
    }
  };

  if (showMigration) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => {
            setShowMigration(false);
            fetchClients();
          }}
        >
          ← Voltar para Lista
        </Button>
        <ClientsMigrationTool />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Users className="h-6 w-6" />
          Clientes
          {/* ✅ CONTADOR DE CLIENTES ADICIONADO AQUI */}
          {!isLoading && (
            <span className="ml-2 text-sm font-normal text-muted-foreground bg-gray-100 px-2.5 py-0.5 rounded-full dark:bg-gray-800 border">
              {clients.length}
            </span>
          )}
        </h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowMigration(true)} className="w-full sm:w-auto">
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Importar/Migrar Dados
          </Button>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <Input
          placeholder="Buscar por nome, telefone ou CPF..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full sm:max-w-sm"
        />
        {/* Opcional: Mostrar contador de resultados da busca se estiver filtrando */}
        {searchTerm && (
          <span className="text-sm text-muted-foreground">
            Encontrados: {filteredClients.length}
          </span>
        )}
      </div>

      {/* Visão Mobile (Cards) */}
      <div className="md:hidden space-y-4">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Carregando...</div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Nenhum cliente encontrado.</div>
        ) : (
          filteredClients.map((client) => (
            <div key={client.id} className="bg-card rounded-lg border p-4 shadow-sm flex flex-col gap-3">
              <div className="flex justify-between items-start gap-2">
                <div className="overflow-hidden">
                  <p className="font-semibold text-base truncate">{client.name}</p>
                  <p className="text-sm text-muted-foreground">{client.phone}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 bg-slate-50 dark:bg-accent"
                    onClick={() => setEditingClient(client)}
                  >
                    <Edit className="h-4 w-4 text-primary" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 bg-slate-50 dark:bg-accent hover:bg-red-50 hover:text-red-500"
                    onClick={() => deleteClient(client.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm pt-3 border-t border-slate-100 dark:border-border text-slate-600 dark:text-muted-foreground">
                {client.cpf && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium">CPF:</span>
                    {client.cpf}
                  </div>
                )}
                {client.city && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Cidade:</span>
                    {client.city}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Visão Desktop (Tabela) */}
      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Cidade</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Nenhum cliente encontrado.
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell className="font-medium">{client.name}</TableCell>
                  <TableCell>{client.phone}</TableCell>
                  <TableCell>{client.cpf || "-"}</TableCell>
                  <TableCell>{client.city || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingClient(client)}
                      >
                        <Edit className="h-4 w-4 text-muted-foreground" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => deleteClient(client.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editingClient && (
        <EditClientModal
          isOpen={!!editingClient}
          onClose={() => setEditingClient(null)}
          onSave={handleUpdateClient}
          clientData={editingClient}
        />
      )}
    </div>
  );
}

export default ClientsTab;
